import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Skeleton } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, RefreshCw } from "lucide-react";

const PRIORITY_STYLE = {
  high: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400",
  medium: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400",
  low: "bg-muted text-muted-foreground",
};

export const Recommendations = () => {
  const [rec, setRec] = useState(undefined);
  const [generating, setGenerating] = useState(false);

  const load = () => api.get("/tools/recommendations").then(({ data }) => setRec(data.recommendation));
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/tools/recommendations/generate");
      setRec(data.recommendation);
      toast.success("Fresh recommendations generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const toggle = async (item) => {
    await api.patch(`/tools/recommendations/items/${item.item_id}`, { done: !item.done });
    setRec((r) => ({ ...r, items: r.items.map((i) => i.item_id === item.item_id ? { ...i, done: !i.done } : i) }));
  };

  if (rec === undefined) return <Skeleton className="h-64" />;

  const done = rec?.items?.filter((i) => i.done).length || 0;

  return (
    <div className="space-y-5 max-w-3xl" data-testid="recommendations-tool">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-muted-foreground">
          Claude studies your live Amazon data and produces a detailed action checklist — generated only when you ask.
          {rec && ` Latest: ${rec.date} · ${done}/${rec.items.length} done.`}
        </p>
        <Button size="sm" className="rounded-full" onClick={generate} disabled={generating} data-testid="generate-recs-button">
          {generating ? <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" aria-hidden="true" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />}
          {generating ? "Analyzing your data…" : "Get Recommendations"}
        </Button>
      </div>
      {!rec ? (
        <div className="bg-card border border-border rounded-lg p-10 text-center">
          <Sparkles className="h-8 w-8 mx-auto text-muted-foreground mb-3" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">No recommendations yet — click "Get Recommendations".</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="recs-checklist">
          {["high", "medium", "low"].map((pri) => {
            const items = rec.items.filter((i) => i.priority === pri);
            if (items.length === 0) return null;
            return (
              <div key={pri} className="space-y-2">
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground pt-3">{pri} priority</p>
                {items.map((i) => (
                  <label key={i.item_id} className={`flex items-start gap-3 bg-card border border-border rounded-lg p-4 cursor-pointer hover:border-blue-600/50 transition-colors ${i.done ? "opacity-60" : ""}`} data-testid={`rec-item-${i.item_id}`}>
                    <input type="checkbox" checked={i.done} onChange={() => toggle(i)} className="mt-0.5" data-testid={`rec-check-${i.item_id}`} />
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${i.done ? "line-through" : ""}`}>{i.text}</p>
                      {i.why && <p className="text-xs text-muted-foreground mt-1.5"><strong className="text-foreground">Why:</strong> {i.why}</p>}
                      {i.how?.length > 0 && (
                        <ul className="mt-1.5 space-y-0.5">
                          {i.how.map((step, idx) => (
                            <li key={idx} className="text-xs text-muted-foreground flex gap-1.5"><span className="text-blue-600 font-semibold">{idx + 1}.</span> {step}</li>
                          ))}
                        </ul>
                      )}
                      <Badge className={`${PRIORITY_STYLE[i.priority]} border-0 font-semibold text-[10px] mt-2`}>{i.category}</Badge>
                    </div>
                  </label>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
